/**
 * Générateur — Calculer des effectifs et des fréquences (exprimées sous forme
 * décimale, fractionnaire ou de pourcentage).
 * Attendu ID : 972 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Version conforme à la spécification 972 (2026-09-21).
 * Supprimées : « Les 60 lancers de dé » (dépouillement d'une grille = objectif
 * 971, question sur les nombres pairs = regroupement en première question,
 * QCM de fluctuation hors objectif) et « Le temps de trajet des élèves »
 * (lecture d'un diagramme = 973, questions « au moins / au plus » = effectifs
 * cumulés exclus). Conservée et recalibrée : « Les pointures du magasin »
 * (les trois écritures d'une même fréquence ; les questions cumulées de la
 * saisie et du V/F sont remplacées).
 *
 * Plan de cartes :
 *   « La fréquence en fraction » (niveau 1) — tableau d'effectifs complet,
 *     total donné (20 à 400), une fréquence en fraction non réduite.
 *   « Les pointures du magasin » (niveau 2) — total ∈ {20, 25, 40, 50}, la
 *     MÊME fréquence en fraction, en décimal et en pourcentage (trois champs),
 *     chaque écriture nommée par la consigne.
 *   « L'effectif effacé » (niveau 3) — un effectif masqué, total donné : l'élève
 *     retrouve l'effectif par complément puis une fréquence d'une AUTRE
 *     modalité, dans l'écriture nommée (total diviseur d'une puissance de dix).
 *   « Du pourcentage à l'effectif » (niveau 4) — un pourcentage et le total
 *     donnés → effectif ; puis une fréquence NON décimale avec arrondi annoncé
 *     (millième ou dixième de pour cent), introduite par un signe ≈ ; deux
 *     modalités regroupées sous un nom commun dans l'énoncé (piège du
 *     dénominateur).
 *
 * Spécification appliquée :
 *   Bornes : effectif total 20..400 (niveau 2 : {20, 25, 40, 50} ; niveau 3 :
 *     diviseurs d'une puissance de dix {20, 25, 40, 50, 80, 100, 125, 200, 250,
 *     400} ; niveau 4 : multiples de 3 {30, 60, 90, 120, 150, 180, 240, 300, 360}
 *     pour qu'une fréquence non décimale existe) ; modalités 3..6 ; effectif
 *     d'une modalité ≥ 1 et < total ; au plus un effectif masqué ; arrondi de
 *     la fréquence décimale : 3 décimales, du pourcentage : 1 décimale (niveau
 *     4 uniquement, les autres niveaux sont exacts).
 *   Exclusions : aucune fréquence 0 ou 1 ; aucun effectif cumulé (plus de
 *     « au moins / au plus ») ; regroupement de modalités seulement au niveau
 *     4 et jamais comme la question posée ; écriture décimale exacte seulement
 *     avec un total diviseur d'une puissance de dix ; la modalité masquée
 *     n'est jamais celle dont on demande la fréquence.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - diviser par l'effectif d'un sous-groupe → `diviser_par_sous_groupe`
 *     - total = somme des seuls effectifs visibles → `total_sans_le_masque`
 *     - effectif à la place de la fréquence (ou l'inverse) → `effectif_pour_frequence`
 *     - arrondi présenté comme exact (signe =) → `arrondi_pris_pour_exact`
 *     - facteur 100 en trop ou en moins → `facteur_cent`
 *     - nombre de modalités pris pour dénominateur → `modalites_pour_denominateur`
 *   Graduation → cartes : 1 « La fréquence en fraction », 2 « Les pointures du
 *     magasin », 3 « L'effectif effacé », 4 « Du pourcentage à l'effectif ».
 *   Non tranché : la fraction attendue est « non réduite » mais le vérificateur
 *     accepte toute fraction de même valeur (liste finie dans
 *     reponses_acceptees) ; le signe ≈ est dans la phrase à trous, l'élève ne
 *     le tape pas ; le tableau des pointures reste lu sur un diagramme en
 *     barres à graduation unitaire.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, tex, fracTex,
} from '../../../utils';
import { imgElement, svgBarres, ligneTable, tableEnonce } from './svgStats';

const ATTENDU_ID = 972;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FRACTION = 'La fréquence en fraction';
const L_POINTURES = 'Les pointures du magasin';
const L_MASQUE = 'L\'effectif effacé';
const L_POURCENT = 'Du pourcentage à l\'effectif';

const TOTAUX_DECIMAUX = [20, 25, 40, 50, 80, 100, 125, 200, 250, 400];
const TOTAUX_NON_DECIMAUX = [30, 60, 90, 120, 150, 180, 240, 300, 360];

// ─── Helpers numériques (entiers, jamais de flottant) ────────

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

/** n/d est-il un nombre décimal ? (dénominateur réduit sans autre facteur que 2 et 5) */
const estDecimal = (n, d) => {
    let q = d / pgcd(n, d);
    while (q % 2 === 0) q /= 2;
    while (q % 5 === 0) q /= 5;
    return q === 1;
};

/** n/d en % exact, écriture française (d divise 10 000). */
const pctFr = (n, d) => {
    const c = (n * 10000) / d;
    const e = Math.floor(c / 100), r = c % 100;
    return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`;
};

/** n/d en décimal exact (d divise 1 000) : "0,24", "0,375". */
const decimalFr = (n, d) => {
    const milliemes = (n * 1000) / d;
    const e = Math.floor(milliemes / 1000), r = milliemes % 1000;
    if (r === 0) return String(e);
    return `${e},${String(r).padStart(3, '0').replace(/0+$/, '')}`;
};

/** Entier de millièmes → écriture française sans zéros finaux ("233" → "0,233"). */
const milliemesFr = (m) => {
    const e = Math.floor(m / 1000), r = m % 1000;
    if (r === 0) return String(e);
    return `${e},${String(r).padStart(3, '0').replace(/0+$/, '')}`;
};

/** Entier de dixièmes → écriture française ("233" → "23,3"). */
const dixiemesFr = (m) => {
    const e = Math.floor(m / 10), r = m % 10;
    return r === 0 ? String(e) : `${e},${r}`;
};

/** Toutes les fractions égales à a/N de dénominateur diviseur de N (liste finie, auditable). */
const ecrituresFraction = (a, N) => {
    const g = pgcd(a, N);
    const out = [];
    for (let d = 1; d <= g; d++) if (g % d === 0) out.push(`${a / d}/${N / d}`);
    return out;
};

// ─── Séries de contexte (tableaux d'effectifs) ───────────────

const SERIES = [
    { sujet: 'le sport pratiqué en club par les élèves d\'un collège', entete: 'Sport', pool: ['Football', 'Basket', 'Judo', 'Natation', 'Danse', 'Tennis'] },
    { sujet: 'le moyen de transport des élèves pour venir au collège', entete: 'Transport', pool: ['À pied', 'Vélo', 'Bus', 'Voiture', 'Trottinette', 'Train'] },
    { sujet: 'la langue vivante choisie par les élèves d\'un collège', entete: 'Langue', pool: ['Anglais', 'Espagnol', 'Allemand', 'Italien', 'Portugais', 'Chinois'] },
    { sujet: 'le dessert choisi à la cantine par les élèves', entete: 'Dessert', pool: ['Yaourt', 'Fruit', 'Compote', 'Gâteau', 'Glace', 'Fromage'] },
    { sujet: 'la couleur préférée des élèves d\'un collège', entete: 'Couleur', pool: ['Rouge', 'Bleu', 'Vert', 'Jaune', 'Violet', 'Orange'] },
    { sujet: 'le genre de livre préféré des lecteurs d\'une bibliothèque', entete: 'Genre', pool: ['Roman', 'BD', 'Manga', 'Documentaire', 'Poésie', 'Théâtre'] },
];

/** k effectifs ≥ 1 de somme N, aucun égal à N (k ≥ 3), tirage borné + repli. */
const repartir = (N, k) => {
    const lo = Math.max(1, Math.floor(N / (3 * k)));
    const hi = Math.max(lo + 1, Math.ceil((2 * N) / k));
    for (let essai = 0; essai < 80; essai++) {
        const effs = Array.from({ length: k - 1 }, () => randInt(lo, hi));
        const dernier = N - effs.reduce((a, b) => a + b, 0);
        if (dernier < 1 || dernier > hi) continue;
        return shuffle([...effs, dernier]);
    }
    const base = Math.floor(N / k);
    const effs = Array.from({ length: k }, () => base);
    effs[0] += N - base * k;
    return effs;
};

const tirerSerie = (N, k) => {
    const s = randomChoice(SERIES);
    const noms = shuffle(s.pool).slice(0, k);
    return { N, k, sujet: s.sujet, entete: s.entete, noms, effs: repartir(N, k) };
};

const tableSerie = (d, idxMasque = -1) => tableEnonce(
    ligneTable(d.entete, [...d.noms, 'Total']),
    ligneTable('Effectif', [...d.effs.map((e, i) => (i === idxMasque ? '<strong>?</strong>' : e)), d.N]),
);

const introSerie = (d) => `<p>Le tableau donne ${d.sujet}. L'effectif total est ${d.N}.</p>${tableSerie(d)}`;

const paramsSerie = (d, niveau, extra = {}) => ({
    niveau,
    effectif_total_de_la_serie: d.N,
    nombre_de_modalites: d.k,
    effectif_d_une_modalite: d.effs,
    somme_effectifs: d.effs.reduce((a, b) => a + b, 0),
    nombre_d_effectifs_masques: 0,
    ...extra,
});

// ═════════════════════════════════════════════════════════════
// CARTE « La fréquence en fraction » (niveau 1)
// ═════════════════════════════════════════════════════════════

const tirerFraction = () => tirerSerie(randInt(20, 400), randInt(3, 6));

const genFractionSaisie = () => {
    const d = tirerFraction();
    const i = randInt(0, d.k - 1);
    const ecritures = ecrituresFraction(d.effs[i], d.N);
    const payload = makeSaisie(`${d.effs[i]}/${d.N}`,
        `${introSerie(d)}<p>Donne la fréquence de la réponse « ${d.noms[i]} » sous forme d'une fraction, tapée avec une barre, `
        + `par exemple <code>7/${d.N}</code>. Il n'est pas demandé de la simplifier.</p>`,
        { ...opts(L_FRACTION), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = paramsSerie(d, 1, { ecriture: 'fraction', erreurVisee: 'modalites_pour_denominateur' });
    return payload;
};

const genFractionQcm = () => {
    const d = tirerFraction();
    const i = randInt(0, d.k - 1);
    const a = d.effs[i];
    const bonne = fracTex(a, d.N);
    const dist = [
        { code: 'modalites_pour_denominateur', valeur: fracTex(a, d.k) },
        { code: 'effectif_pour_frequence', valeur: tex(String(a)) },
        { code: 'facteur_cent', valeur: fracTex(100 * a, d.N) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introSerie(d)}<p>Quelle fraction donne la fréquence de la réponse « ${d.noms[i]} » ?</p>`,
        opts(L_FRACTION));
    payload._meta.params = paramsSerie(d, 1, { ecriture: 'fraction', distracteurs: dist });
    return payload;
};

const genFractionVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerFraction();
        const [i, j, l] = shuffle(d.noms.map((_, x) => x)).slice(0, 3);
        const codes = [];
        const lignes = [];
        const v1 = randInt(0, 1) === 0;
        lignes.push([`La fréquence de la réponse « ${d.noms[i]} » est ${v1 ? fracTex(d.effs[i], d.N) : fracTex(d.effs[i], d.k)}.`, v1 ? 0 : 1]);
        if (!v1) codes.push('modalites_pour_denominateur');
        const v2 = randInt(0, 1) === 0;
        lignes.push([v2
            ? `L'effectif de la réponse « ${d.noms[j]} » est ${d.effs[j]}.`
            : `La fréquence de la réponse « ${d.noms[j]} » est ${d.effs[j]}.`, v2 ? 0 : 1]);
        if (!v2) codes.push('effectif_pour_frequence');
        const v3 = randInt(0, 1) === 0;
        lignes.push([`La fréquence de la réponse « ${d.noms[l]} » est ${v3 ? fracTex(d.effs[l], d.N) : fracTex(100 * d.effs[l], d.N)}.`, v3 ? 0 : 1]);
        if (!v3) codes.push('facteur_cent');
        const v4 = randInt(0, 1) === 0;
        lignes.push([v4
            ? `Pour calculer une fréquence, on divise l'effectif de la réponse par l'effectif total, ${d.N}.`
            : `Pour calculer une fréquence, on divise l'effectif de la réponse par le nombre de réponses possibles, ${d.k}.`, v4 ? 0 : 1]);
        if (!v4) codes.push('modalites_pour_denominateur');
        const nbVrai = lignes.filter((x) => x[1] === 0).length;
        if (nbVrai === 0 || nbVrai === 4) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
            `${introSerie(d)}<p>Vrai ou faux ?</p>`, opts(L_FRACTION));
        payload._meta.params = paramsSerie(d, 1, { ecriture: 'fraction', distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_972 : V/F niveau 1 introuvable');
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les pointures du magasin » (niveau 2)
// ═════════════════════════════════════════════════════════════

/** 6 pointures consécutives, total ∈ {20, 25, 40, 50} (fréquences décimales finies), effectifs ≥ 1. */
const tirerPointures = () => {
    const debut = randInt(36, 38);
    const pointures = Array.from({ length: 6 }, (_, i) => debut + i);
    for (let essai = 0; essai < 120; essai++) {
        const total = randomChoice([20, 25, 40, 50]);
        const maxEff = Math.ceil(total / 4);
        const effs = Array.from({ length: 5 }, () => randInt(1, maxEff));
        const dernier = total - effs.reduce((a, b) => a + b, 0);
        if (dernier < 1 || dernier > maxEff + 2) continue;
        effs.push(dernier);
        return { pointures, effs, total };
    }
    return { pointures: [37, 38, 39, 40, 41, 42], effs: [5, 7, 6, 2, 4, 1], total: 25 };
};

const svgPointures = (d) => imgElement(svgBarres(d.pointures.map(String), d.effs,
    { yStep: 1, yLabel: 'Effectifs', xLabel: 'Pointures', batons: true }));

const INTRO_POINTURES = '<p>Le diagramme en barres donne les pointures des clients d\'un magasin de chaussures.</p>';

const paramsPointures = (d, extra = {}) => ({
    niveau: 2,
    effectif_total_de_la_serie: d.total,
    nombre_de_modalites: 6,
    effectif_d_une_modalite: d.effs,
    somme_effectifs: d.effs.reduce((a, b) => a + b, 0),
    nombre_d_effectifs_masques: 0,
    total_decimal: TOTAUX_DECIMAUX.includes(d.total),
    ...extra,
});

const genPointuresTrous = () => {
    const d = tirerPointures();
    // LA fréquence sous trois formes, chacune typée (fraction non simplifiée :
    // l'effectif sert de numérateur). Lignes KaTeX globales, fraction à deux
    // cases avec étais ; « % » s'écrit \% en formule.
    const i = randInt(0, 5);
    const phrase = `$\\text{Fréquence de la pointure ${d.pointures[i]}, en fraction sur ${d.total} : } `
        + `\\dfrac{{{${d.effs[i]}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${d.total}}}}$\n`
        + `$\\text{En écriture décimale : } {{${decimalFr(d.effs[i], d.total)}}}$\n`
        + `$\\text{En pourcentage : } {{${pctFr(d.effs[i], d.total)}}}\\,\\%$`;
    const texte = `${INTRO_POINTURES}<p>L'effectif total est ${d.total}. Complète : la même fréquence en fraction non simplifiée, `
        + 'en écriture décimale avec une virgule, puis en pourcentage.</p>';
    const payload = {
        ...makeTexteTrou(phrase, texte, { ...opts(L_POINTURES), mode: 'saisie', strict: false }),
        elements_additionnels: svgPointures(d),
    };
    payload._meta.params = paramsPointures(d, { ecriture: 'trois', erreurVisee: 'facteur_cent' });
    return payload;
};

const genPointuresSaisie = () => {
    const d = tirerPointures();
    const i = randInt(0, 5);
    const a = d.effs[i];
    const p = d.pointures[i];
    const variante = randInt(0, 2);
    let payload;
    let ecriture;
    if (variante === 0) {
        ecriture = 'fraction';
        payload = makeSaisie(`${a}/${d.total}`,
            `${INTRO_POINTURES}<p>L'effectif total est ${d.total}. Donne la fréquence de la pointure ${p} sous forme d'une fraction, `
            + `tapée avec une barre, par exemple <code>4/${d.total}</code>. Il n'est pas demandé de la simplifier.</p>`,
            { ...opts(L_POINTURES), comparaison: 'expression', reponses_acceptees: ecrituresFraction(a, d.total) });
    } else if (variante === 1) {
        ecriture = 'decimale';
        payload = makeSaisie(decimalFr(a, d.total),
            `${INTRO_POINTURES}<p>L'effectif total est ${d.total}. Donne la fréquence de la pointure ${p} en écriture décimale, `
            + 'avec une virgule, par exemple <code>0,15</code>.</p>',
            { ...opts(L_POINTURES), strict: false });
    } else {
        ecriture = 'pourcentage';
        payload = makeSaisie(pctFr(a, d.total),
            `${INTRO_POINTURES}<p>L'effectif total est ${d.total}. Donne la fréquence de la pointure ${p} en pourcentage. `
            + 'Utilise une virgule si besoin.</p>',
            { ...opts(L_POINTURES), strict: false, suffixe: '%' });
    }
    payload.elements_additionnels = svgPointures(d);
    payload._meta.params = paramsPointures(d, { ecriture, erreurVisee: 'effectif_pour_frequence' });
    return payload;
};

const genPointuresVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerPointures();
        const [i, j, l] = shuffle([0, 1, 2, 3, 4, 5]).slice(0, 3);
        const codes = [];
        const lignes = [];
        const v1 = randInt(0, 1) === 0;
        lignes.push([`La fréquence de la pointure ${d.pointures[i]} est ${v1 ? fracTex(d.effs[i], d.total) : fracTex(d.effs[i], 6)}.`, v1 ? 0 : 1]);
        if (!v1) codes.push('modalites_pour_denominateur');
        const v2 = randInt(0, 1) === 0;
        lignes.push([`En écriture décimale, la fréquence de la pointure ${d.pointures[j]} est ${v2 ? decimalFr(d.effs[j], d.total) : d.effs[j]}.`, v2 ? 0 : 1]);
        if (!v2) codes.push('effectif_pour_frequence');
        const v3 = randInt(0, 1) === 0;
        lignes.push([`En pourcentage, la fréquence de la pointure ${d.pointures[l]} est ${v3 ? pctFr(d.effs[l], d.total) : decimalFr(d.effs[l], d.total)} %.`, v3 ? 0 : 1]);
        if (!v3) codes.push('facteur_cent');
        const v4 = randInt(0, 1) === 0;
        lignes.push([v4
            ? `${fracTex(d.effs[i], d.total)}, ${decimalFr(d.effs[i], d.total)} et ${pctFr(d.effs[i], d.total)} % sont trois écritures de la même fréquence.`
            : `Pour trouver la fréquence de la pointure ${d.pointures[i]}, on divise ${d.effs[i]} par 6, le nombre de pointures.`, v4 ? 0 : 1]);
        if (!v4) codes.push('modalites_pour_denominateur');
        const nbVrai = lignes.filter((x) => x[1] === 0).length;
        if (nbVrai === 0 || nbVrai === 4) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
            `${INTRO_POINTURES}<p>L'effectif total est ${d.total}. Vrai ou faux ?</p>`,
            { ...opts(L_POINTURES), elements_additionnels: svgPointures(d) });
        payload._meta.params = paramsPointures(d, { ecriture: 'trois', distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_972 : V/F pointures introuvable');
};

// ═════════════════════════════════════════════════════════════
// CARTE « L'effectif effacé » (niveau 3)
// ═════════════════════════════════════════════════════════════

const ECRITURES = ['fraction', 'decimale', 'pourcentage'];

/** Série à total diviseur d'une puissance de dix, un effectif masqué (m), une autre modalité interrogée (j). */
const tirerMasque = () => {
    const d = tirerSerie(randomChoice(TOTAUX_DECIMAUX), randInt(3, 6));
    const [m, j] = shuffle(d.noms.map((_, x) => x)).slice(0, 2);
    return { ...d, m, j, ecriture: randomChoice(ECRITURES) };
};

const introMasque = (d) =>
    `<p>Le tableau donne ${d.sujet}. L'effectif total est ${d.N}, mais un effectif a été effacé.</p>${tableSerie(d, d.m)}`;

const paramsMasque = (d, extra = {}) => paramsSerie(d, 3, {
    nombre_d_effectifs_masques: 1,
    masque_egal_interroge: d.m === d.j,
    total_decimal: TOTAUX_DECIMAUX.includes(d.N),
    ecriture: d.ecriture,
    ...extra,
});

/** Ligne KaTeX de la fréquence de la modalité j dans l'écriture demandée. */
const ligneFrequence = (d, b) => {
    const nom = d.noms[d.j];
    if (d.ecriture === 'fraction') {
        return `$\\text{Fréquence de la réponse ${nom}, en fraction sur ${d.N} : } `
            + `\\dfrac{{{${b}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${d.N}}}}$`;
    }
    if (d.ecriture === 'decimale') return `$\\text{Fréquence de la réponse ${nom}, en écriture décimale : } {{${decimalFr(b, d.N)}}}$`;
    return `$\\text{Fréquence de la réponse ${nom}, en pourcentage : } {{${pctFr(b, d.N)}}}\\,\\%$`;
};

const genMasqueTrous = () => {
    const d = tirerMasque();
    const phrase = `$\\text{Effectif de la réponse ${d.noms[d.m]} : } {{${d.effs[d.m]}}}$\n${ligneFrequence(d, d.effs[d.j])}`;
    const consigne = {
        fraction: 'puis la fréquence demandée en fraction non simplifiée',
        decimale: 'puis la fréquence demandée en écriture décimale, avec une virgule',
        pourcentage: 'puis la fréquence demandée en pourcentage',
    }[d.ecriture];
    const payload = makeTexteTrou(phrase,
        `${introMasque(d)}<p>Retrouve l'effectif effacé, ${consigne}.</p>`,
        { ...opts(L_MASQUE), mode: 'saisie', strict: false });
    payload._meta.params = paramsMasque(d, { erreurVisee: 'total_sans_le_masque' });
    return payload;
};

const genMasqueQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerMasque();
        const b = d.effs[d.j];
        const visibles = d.N - d.effs[d.m];
        if (randInt(0, 1) === 0) {
            // L'effectif effacé
            const bonne = d.effs[d.m];
            const dist = [
                { code: 'total_sans_le_masque', valeur: visibles },
                { code: 'modalites_pour_denominateur', valeur: d.k },
                { code: 'effectif_pour_frequence', valeur: d.N },
            ];
            const valeurs = [bonne, ...dist.map((x) => x.valeur)];
            if (new Set(valeurs).size !== 4) continue;
            const payload = makeQcm(valeurs.map(String), String(bonne),
                `${introMasque(d)}<p>Quel est l'effectif effacé, celui de la réponse « ${d.noms[d.m]} » ?</p>`,
                opts(L_MASQUE));
            payload._meta.params = paramsMasque(d, { distracteurs: dist });
            return payload;
        }
        // La fréquence d'une autre modalité, en fraction (toutes les valeurs exactes)
        const bonne = fracTex(b, d.N);
        const dist = [
            { code: 'total_sans_le_masque', valeur: fracTex(b, visibles) },
            { code: 'modalites_pour_denominateur', valeur: fracTex(b, d.k) },
            { code: 'effectif_pour_frequence', valeur: tex(String(b)) },
        ];
        if (visibles === d.k) continue;
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${introMasque(d)}<p>Quelle fraction donne la fréquence de la réponse « ${d.noms[d.j]} » ?</p>`,
            opts(L_MASQUE));
        payload._meta.params = paramsMasque(d, { ecriture: 'fraction', distracteurs: dist });
        return payload;
    }
    throw new Error('gen_972 : QCM niveau 3 introuvable');
};

const genMasqueVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerMasque();
        const b = d.effs[d.j];
        const visibles = d.N - d.effs[d.m];
        const codes = [];
        const lignes = [];
        const v1 = randInt(0, 1) === 0;
        lignes.push([`L'effectif de la réponse « ${d.noms[d.m]} » est ${v1 ? d.effs[d.m] : d.N - visibles + d.k}.`, v1 ? 0 : 1]);
        if (!v1) codes.push('modalites_pour_denominateur');
        const v2 = randInt(0, 1) === 0;
        lignes.push([v2
            ? `Pour calculer une fréquence, on divise par l'effectif total, ${d.N}.`
            : `Pour calculer une fréquence, on divise par la somme des effectifs lisibles, ${visibles}.`, v2 ? 0 : 1]);
        if (!v2) codes.push('total_sans_le_masque');
        const v3 = randInt(0, 1) === 0;
        lignes.push([`La fréquence de la réponse « ${d.noms[d.j]} » est ${v3 ? fracTex(b, d.N) : fracTex(b, visibles)}.`, v3 ? 0 : 1]);
        if (!v3) codes.push('total_sans_le_masque');
        const v4 = randInt(0, 1) === 0;
        lignes.push([`En pourcentage, la fréquence de la réponse « ${d.noms[d.j]} » est ${v4 ? pctFr(b, d.N) : decimalFr(b, d.N)} %.`, v4 ? 0 : 1]);
        if (!v4) codes.push('facteur_cent');
        const nbVrai = lignes.filter((x) => x[1] === 0).length;
        if (nbVrai === 0 || nbVrai === 4) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
            `${introMasque(d)}<p>Vrai ou faux ?</p>`, opts(L_MASQUE));
        payload._meta.params = paramsMasque(d, { distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_972 : V/F niveau 3 introuvable');
};

// ═════════════════════════════════════════════════════════════
// CARTE « Du pourcentage à l'effectif » (niveau 4)
// ═════════════════════════════════════════════════════════════

/**
 * Total multiple de 3 (une fréquence non décimale existe), pourcentage p
 * entier de la modalité x tel que p × N / 100 soit entier ; modalité y ≠ x
 * dont la fréquence n'est pas décimale ; les modalités x et z sont regroupées
 * sous un nom commun (piège du dénominateur).
 */
const tirerPourcent = () => {
    for (let essai = 0; essai < 200; essai++) {
        const N = randomChoice(TOTAUX_NON_DECIMAUX);
        const k = randInt(3, 6);
        const pas = 100 / pgcd(N, 100);             // p doit être multiple de « pas »
        const pMax = Math.floor(60 / pas) * pas;
        const p = pas * randInt(1, pMax / pas);
        const e = (p * N) / 100;
        if (e < 1 || e >= N || e === p) continue;
        const reste = N - e;
        if (reste < k - 1) continue;
        const autres = repartir(reste, k - 1);
        if (autres.some((a) => a < 1)) continue;
        const s = randomChoice(SERIES);
        const noms = shuffle(s.pool).slice(0, k);
        const x = randInt(0, k - 1);
        const effs = [...autres.slice(0, x), e, ...autres.slice(x)];
        const candidatsY = effs.map((b, i) => i).filter((i) => i !== x && !estDecimal(effs[i], N));
        if (candidatsY.length === 0) continue;
        const y = randomChoice(candidatsY);
        const z = randomChoice(effs.map((_, i) => i).filter((i) => i !== x));
        return { N, k, p, e, x, y, z, noms, effs, sujet: s.sujet, entete: s.entete };
    }
    throw new Error('gen_972 : série de niveau 4 introuvable');
};

const introPourcent = (d) => {
    const groupe = `${d.noms[d.x]} et ${d.noms[d.z]}`;
    return `<p>Le tableau donne ${d.sujet}. L'effectif total est ${d.N}. `
        + `On sait que ${d.p} % des réponses sont « ${d.noms[d.x]} », mais cet effectif a été effacé.</p>`
        + tableSerie(d, d.x)
        + `<p>Les réponses « ${groupe} » sont regroupées sous le nom « ${d.noms[d.x]} ou ${d.noms[d.z]} ».</p>`;
};

const paramsPourcent = (d, extra = {}) => paramsSerie(d, 4, {
    nombre_d_effectifs_masques: 1,
    effectif_a_calculer_a_partir_d_une_frequence: d.e,
    pourcentage_donne: d.p,
    produit_entier: (d.p * d.N) % 100 === 0,
    frequence_y_decimale: estDecimal(d.effs[d.y], d.N),
    arrondi_de_la_frequence_decimale: 3,
    arrondi_du_pourcentage: 1,
    ...extra,
});

const arrondis = (d) => {
    const b = d.effs[d.y];
    return {
        milliemes: milliemesFr(Math.round((b * 1000) / d.N)),
        dixiemesPct: dixiemesFr(Math.round((b * 1000) / d.N)),
    };
};

const genPourcentTrous = () => {
    const d = tirerPourcent();
    const r = arrondis(d);
    const enPct = randInt(0, 1) === 0;
    const ligne2 = enPct
        ? `$\\text{Fréquence de la réponse ${d.noms[d.y]}, en pourcentage arrondi au dixième : } \\approx {{${r.dixiemesPct}}}\\,\\%$`
        : `$\\text{Fréquence de la réponse ${d.noms[d.y]}, en écriture décimale arrondie au millième : } \\approx {{${r.milliemes}}}$`;
    const phrase = `$\\text{Effectif de la réponse ${d.noms[d.x]} : } {{${d.e}}}$\n${ligne2}`;
    const payload = makeTexteTrou(phrase,
        `${introPourcent(d)}<p>Calcule l'effectif effacé à partir du pourcentage, puis la fréquence demandée. `
        + `Cette fréquence n'est pas un nombre décimal : donne-la ${enPct ? 'en pourcentage arrondi au dixième' : 'arrondie au millième'}, `
        + 'le signe ≈ est déjà écrit.</p>',
        { ...opts(L_POURCENT), mode: 'saisie', strict: false });
    payload._meta.params = paramsPourcent(d, { ecriture: enPct ? 'pourcentage' : 'decimale', erreurVisee: 'arrondi_pris_pour_exact' });
    return payload;
};

const genPourcentQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerPourcent();
        const r = arrondis(d);
        const variante = randInt(0, 2);
        if (variante === 0) {
            const bonne = d.e;
            const dist = [
                { code: 'effectif_pour_frequence', valeur: d.p },
                { code: 'facteur_cent', valeur: d.p * d.N },
                { code: 'modalites_pour_denominateur', valeur: d.k },
            ];
            const valeurs = [bonne, ...dist.map((v) => v.valeur)];
            if (new Set(valeurs).size !== 4) continue;
            const payload = makeQcm(valeurs.map(String), String(bonne),
                `${introPourcent(d)}<p>Quel est l'effectif de la réponse « ${d.noms[d.x]} » ?</p>`, opts(L_POURCENT));
            payload._meta.params = paramsPourcent(d, { distracteurs: dist });
            return payload;
        }
        if (variante === 1) {
            const bonne = `≈ ${r.milliemes}`;
            const dist = [
                { code: 'arrondi_pris_pour_exact', valeur: `= ${r.milliemes}` },
                { code: 'facteur_cent', valeur: `≈ ${r.dixiemesPct}` },
                { code: 'facteur_cent', valeur: `= ${r.dixiemesPct}` },
            ];
            const payload = makeQcm([bonne, ...dist.map((v) => v.valeur)], bonne,
                `${introPourcent(d)}<p>La fréquence de la réponse « ${d.noms[d.y]} » vaut ${fracTex(d.effs[d.y], d.N)}, `
                + 'qui n\'est pas un nombre décimal. Quelle est l\'écriture correcte de cette fréquence arrondie au millième ?</p>',
                opts(L_POURCENT));
            payload._meta.params = paramsPourcent(d, { distracteurs: dist });
            return payload;
        }
        const a = d.effs[d.x];
        const bonne = fracTex(a, d.N);
        const dist = [
            { code: 'diviser_par_sous_groupe', valeur: fracTex(a, a + d.effs[d.z]) },
            { code: 'modalites_pour_denominateur', valeur: fracTex(a, d.k) },
            { code: 'effectif_pour_frequence', valeur: tex(String(a)) },
        ];
        if (a + d.effs[d.z] === d.k) continue;
        const payload = makeQcm([bonne, ...dist.map((v) => v.valeur)], bonne,
            `${introPourcent(d)}<p>Une fois l'effectif retrouvé, quelle fraction donne la fréquence de la réponse « ${d.noms[d.x]} » parmi toutes les réponses ?</p>`,
            opts(L_POURCENT));
        payload._meta.params = paramsPourcent(d, { distracteurs: dist });
        return payload;
    }
    throw new Error('gen_972 : QCM niveau 4 introuvable');
};

const genPourcentVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerPourcent();
        const r = arrondis(d);
        const a = d.effs[d.x];
        const codes = [];
        const lignes = [];
        const v1 = randInt(0, 1) === 0;
        lignes.push([`L'effectif de la réponse « ${d.noms[d.x]} » est ${v1 ? d.e : d.p}.`, v1 ? 0 : 1]);
        if (!v1) codes.push('effectif_pour_frequence');
        const v2 = randInt(0, 1) === 0;
        lignes.push([`La fréquence de la réponse « ${d.noms[d.y]} » est ${v2 ? 'environ' : 'exactement'} ${r.milliemes}.`, v2 ? 0 : 1]);
        if (!v2) codes.push('arrondi_pris_pour_exact');
        const v3 = randInt(0, 1) === 0;
        lignes.push([`En pourcentage, la fréquence de la réponse « ${d.noms[d.y]} » est environ ${v3 ? r.dixiemesPct : r.milliemes} %.`, v3 ? 0 : 1]);
        if (!v3) codes.push('facteur_cent');
        const v4 = randInt(0, 1) === 0;
        lignes.push([v4
            ? `La fréquence de la réponse « ${d.noms[d.x]} » se calcule en divisant ${a} par ${d.N}.`
            : `La fréquence de la réponse « ${d.noms[d.x]} » se calcule en divisant ${a} par ${a + d.effs[d.z]}, l'effectif du groupe « ${d.noms[d.x]} ou ${d.noms[d.z]} ».`, v4 ? 0 : 1]);
        if (!v4) codes.push('diviser_par_sous_groupe');
        const nbVrai = lignes.filter((x) => x[1] === 0).length;
        if (nbVrai === 0 || nbVrai === 4) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
            `${introPourcent(d)}<p>Vrai ou faux ?</p>`, opts(L_POURCENT));
        payload._meta.params = paramsPourcent(d, { distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_972 : V/F niveau 4 introuvable');
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    fraction_texte: genFractionSaisie,
    fraction_qcm_unique: genFractionQcm,
    fraction_tableau_vf: genFractionVF,
    pointures_texte_trou_saisie: genPointuresTrous,
    pointures_texte: genPointuresSaisie,
    pointures_tableau_vf: genPointuresVF,
    masque_texte_trou_saisie: genMasqueTrous,
    masque_qcm_unique: genMasqueQcm,
    masque_tableau_vf: genMasqueVF,
    pourcent_texte_trou_saisie: genPourcentTrous,
    pourcent_qcm_unique: genPourcentQcm,
    pourcent_tableau_vf: genPourcentVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'fraction', label: L_FRACTION, formatsList: buildFormats('fraction') },
    { id: 'pointures', label: L_POINTURES, formatsList: buildFormats('pointures') },
    { id: 'masque', label: L_MASQUE, formatsList: buildFormats('masque') },
    { id: 'pourcent', label: L_POURCENT, formatsList: buildFormats('pourcent') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
